import React, {
  useEffect,
  useState,
  createContext,
  PropsWithChildren
} from 'react';
import { useDispatch } from 'react-redux';
import getSizes, { ScreenSize } from './getSizes';
import {
  screenResize,
  screenOrientation
} from '../../../store/src/middleware/ui/ducks/actions';

const throttle = (fn: Function, wait = 300) => {
  let inThrottle: boolean,
    lastFn: ReturnType<typeof setTimeout>,
    lastTime: number;
  return function (this: any) {
    const context = this,
      args = arguments;
    if (!inThrottle) {
      fn.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFn);
      lastFn = setTimeout(
        () => {
          if (Date.now() - lastTime >= wait) {
            fn.apply(context, args);
            lastTime = Date.now();
          }
        },
        Math.max(wait - (Date.now() - lastTime), 0)
      );
    }
  };
};

export interface ContextProps {
  browserType: { name: string; version: string; os: string };
  screenSize: ScreenSize;
}

const UIContext = createContext({
  screenSize: getSizes(window.screen.height, window.screen.width)
});

export default UIContext;

export const UIProvider: React.FC<PropsWithChildren<any>> = ({ children }) => {
  const [size, setSize] = useState(
    getSizes(window.screen.height, window.screen.width)
  );
  const dispatch = useDispatch();

  const uiState = {
    screenSize: size
  };

  dispatch(screenResize({ screenSize: size }));

  useEffect(() => {
    const resizeListener = () => {
      let timeout: number;
      const throttleScreenResize = throttle(() => {
        if (timeout) {
          window.cancelAnimationFrame(timeout);
        }

        timeout = window.requestAnimationFrame(() => {
          const newSize = getSizes(window.screen.height, window.screen.width);
          setSize(newSize);
          dispatch(screenResize({ screenSize: newSize }));
        });
      }, 50);

      window.addEventListener('resize', throttleScreenResize);

      return () => {
        window.removeEventListener('resize', throttleScreenResize);
      };
    };

    resizeListener();
  }, []);

  useEffect(() => {
    const orientationListener = () => {
      let orientationTimeout: number;
      const throttleOrientation = () => {
        if (orientationTimeout) {
          window.cancelAnimationFrame(orientationTimeout);
        }

        orientationTimeout = window.requestAnimationFrame(() => {
          const newSize = getSizes(window.screen.height, window.screen.width);
          setSize(newSize);
          dispatch(screenOrientation({ screenSize: newSize }));
          window.scrollTo(0, 0);
        });
      };
      window.addEventListener('orientationchange', throttleOrientation, false);
      return () => {
        window.removeEventListener('orientationchange', throttleOrientation);
      };
    };

    orientationListener();
  }, []);

  return <UIContext.Provider value={uiState}>{children}</UIContext.Provider>;
};
